import { supabase, withRetry } from "./core.js";
import type { Suitability } from "../types/index.js";

/**
 * Busca o registro de suitability de um cliente.
 * Retorna o objeto mesclado: campos top-level (id, client_id, status, last_updated)
 * + conteúdo do JSONB `data` (nomeCompleto, scoreSuit, perfilResult, etc.)
 * para manter compatibilidade com o restante do frontend.
 */
export async function fetchSuitability(clientId: string): Promise<Suitability | null> {
  return withRetry(async () => {
    const { data: row, error } = await supabase
      .from("suitability")
      .select("*")
      .eq("client_id", clientId)
      .single();
    if (error) {
      if (error.code === "PGRST116") return null;
      throw error;
    }
    // Mescla JSONB `data` nos campos top-level para compatibilidade
    const { data: jsonbData, ...rest } = row;
    return { ...rest, ...(jsonbData || {}), data: jsonbData } as Suitability;
  });
}

/**
 * Busca TODOS os registros de suitability (para painel admin).
 * Retorna um mapa { [client_id]: SuitabilityMerged }.
 */
export async function fetchAllSuitabilities(): Promise<Record<string, Suitability>> {
  return withRetry(async () => {
    const { data: rows, error } = await supabase.from("suitability").select("*");
    if (error) throw error;
    const map: Record<string, Suitability> = {};
    for (const row of rows || []) {
      const { data: jsonbData, ...rest } = row;
      map[row.client_id] = { ...rest, ...(jsonbData || {}), data: jsonbData } as Suitability;
    }
    return map;
  });
}

/**
 * Upsert de suitability: extrai `status` como coluna top-level
 * e encapsula todo o resto no campo JSONB `data`.
 *
 * Schema real: id | client_id | status (text) | data (jsonb) | last_updated | created_at
 */
export async function upsertSuitability(
  clientId: string,
  payload: Partial<Suitability>
): Promise<void> {
  // Separar status (coluna real) do restante (vai para JSONB `data`)
  const {
    status,
    client_id: _cid,
    id: _id,
    created_at: _ca,
    last_updated: _lu,
    data: _d,
    ...formFields
  } = payload as Record<string, unknown>;

  // Mesclar dados existentes no JSONB para não perder campos anteriores
  const { data: existing } = await supabase
    .from("suitability")
    .select("data")
    .eq("client_id", clientId)
    .maybeSingle();

  const mergedData = {
    ...(existing?.data || {}),
    ...formFields,
    ...(status ? { status } : {}), // espelha status dentro do JSONB para leitura legada
    lastUpdate: new Date().toISOString().slice(0, 10),
  };

  const { error } = await supabase.from("suitability").upsert(
    {
      client_id: clientId,
      status: (status as string) || "pendente",
      data: mergedData,
      last_updated: new Date().toISOString(),
    },
    { onConflict: "client_id" }
  );
  if (error) throw error;
}

interface SuitabilityHistoryEntry {
  client_id: string;
  profile_risk?: string;
  score?: number;
  answers?: Record<string, unknown>;
  [key: string]: unknown;
}

export async function logSuitabilityHistory(
  clientId: string,
  entry: Partial<SuitabilityHistoryEntry>
): Promise<void> {
  const { error } = await supabase.from("suitability_history").insert({
    ...entry,
    client_id: clientId,
    created_at: new Date().toISOString(),
  });
  if (error) throw error;
}

/**
 * Aprova o suitability de um cliente.
 * Atualiza `status` (coluna real) para "aprovado" (constraint inclui esse valor)
 * e grava `approved` + `approved_at` dentro do JSONB `data`.
 */
export async function approveSuitability(clientId: string): Promise<Suitability> {
  // Buscar JSONB existente para mesclar
  const { data: existing } = await supabase
    .from("suitability")
    .select("data")
    .eq("client_id", clientId)
    .single();

  const now = new Date().toISOString();
  const mergedData = {
    ...(existing?.data || {}),
    status: "aprovado",
    approved: true,
    approved_at: now,
  };

  const { data: row, error } = await supabase
    .from("suitability")
    .update({
      status: "aprovado",
      data: mergedData,
      last_updated: now,
    })
    .eq("client_id", clientId)
    .select("*")
    .single();
  if (error) throw error;

  // Retorna mesclado para compatibilidade
  const { data: jsonbData, ...rest } = row;
  return { ...rest, ...(jsonbData || {}), data: jsonbData } as Suitability;
}

export async function fetchSuitabilityHistory(
  clientId: string
): Promise<SuitabilityHistoryEntry[]> {
  const { data, error } = await supabase
    .from("suitability_history")
    .select("*")
    .eq("client_id", clientId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}
